import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { useToast } from '../../context/ToastContext';

const DESC_MAX = 60;
const emptyForm = { name: '', slug: '', description: '', parentId: '', sortOrder: 0 };

export default function CategoryManage() {
  const toast = useToast();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get('/api/admin/categories').then((res) => setCategories(res.data));
  };

  useEffect(() => { load(); }, []);

  // 이름으로 slug 자동 생성
  const handleNameChange = (value) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: editingId ? prev.slug : value.toLowerCase().replace(/[^a-z0-9가-힣]/g, '-').replace(/-+/g, '-'),
    }));
  };

  const handleDescChange = (value) => {
    if (value.length <= DESC_MAX) setForm((prev) => ({ ...prev, description: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        slug: form.slug,
        description: form.description || null,
        parentId: form.parentId ? Number(form.parentId) : null,
        sortOrder: Number(form.sortOrder),
      };
      if (editingId) {
        await api.put(`/api/admin/categories/${editingId}`, payload);
        toast('카테고리가 수정되었습니다.', 'success');
      } else {
        await api.post('/api/admin/categories', payload);
        toast('카테고리가 추가되었습니다.', 'success');
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast(err.response?.data?.message || '저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (cat) => {
    setEditingId(cat.id);
    setForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description || '',
      parentId: cat.parent?.id || '',
      sortOrder: cat.sortOrder || 0,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까? 연결된 글의 카테고리가 해제됩니다.')) return;
    try {
      await api.delete(`/api/admin/categories/${id}`);
      toast('카테고리가 삭제되었습니다.', 'info');
      load();
    } catch (err) {
      toast(err.response?.data?.message || '삭제 중 오류가 발생했습니다.', 'error');
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const topLevel = categories.filter((c) => !c.parent);
  const children = (parentId) => categories.filter((c) => c.parent?.id === parentId);

  return (
    <div className="category-manage">
      <h2>카테고리 관리</h2>

      <div className="category-layout">
        {/* 카테고리 트리 */}
        <div className="category-tree">
          <h3>카테고리 목록</h3>
          {categories.length === 0 && <p className="empty-msg">카테고리가 없습니다.</p>}
          <ul className="cat-tree-list">
            {topLevel.map((cat) => (
              <li key={cat.id} className="cat-tree-item">
                <div className="cat-row">
                  <div className="cat-info">
                    <span className="cat-name">{cat.name}</span>
                    <span className="cat-slug">/{cat.slug}</span>
                    {cat.description && (
                      <span className="cat-desc-preview">{cat.description}</span>
                    )}
                  </div>
                  <div className="cat-actions">
                    <button onClick={() => handleEdit(cat)} className="btn-sm">수정</button>
                    <button onClick={() => handleDelete(cat.id)} className="btn-sm btn-danger">삭제</button>
                  </div>
                </div>
                {children(cat.id).map((child) => (
                  <div key={child.id} className="cat-row cat-child-row">
                    <div className="cat-info">
                      <span className="cat-name">└ {child.name}</span>
                      <span className="cat-slug">/{child.slug}</span>
                      {child.description && (
                        <span className="cat-desc-preview">{child.description}</span>
                      )}
                    </div>
                    <div className="cat-actions">
                      <button onClick={() => handleEdit(child)} className="btn-sm">수정</button>
                      <button onClick={() => handleDelete(child.id)} className="btn-sm btn-danger">삭제</button>
                    </div>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        </div>

        {/* 추가/수정 폼 */}
        <div className="category-form-box">
          <h3>{editingId ? '카테고리 수정' : '카테고리 추가'}</h3>
          <form onSubmit={handleSubmit} className="category-form">
            <div className="form-group">
              <label>이름</label>
              <input
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>슬러그 (URL)</label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="cat-desc-label">
                카테고리 소개
                <span className={`cat-desc-count${form.description.length >= DESC_MAX ? ' full' : ''}`}>
                  {form.description.length}/{DESC_MAX}
                </span>
              </label>
              <input
                value={form.description}
                onChange={(e) => handleDescChange(e.target.value)}
                placeholder="한 문장으로 소개해 주세요 (예: 30대가 즐기는 보드게임 이야기)"
                maxLength={DESC_MAX}
              />
              <small className="form-hint">
                목록 페이지에서 카테고리 선택 시 한 줄로 표시됩니다. {DESC_MAX}자 이내로 간결하게 작성해 주세요.
              </small>
            </div>
            <div className="form-group">
              <label>상위 카테고리</label>
              <select
                value={form.parentId}
                onChange={(e) => setForm({ ...form, parentId: e.target.value })}
              >
                <option value="">없음 (최상위)</option>
                {categories
                  .filter((c) => c.id !== editingId)
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label>정렬 순서</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? '저장 중...' : editingId ? '수정 완료' : '추가'}
              </button>
              {editingId && (
                <button type="button" onClick={cancelEdit} className="btn-secondary">
                  취소
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
